import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Add, Delete, Download, Visibility, Close, CloudUpload, Lock, ManageAccounts, Groups, Public, Tune,
  PictureAsPdf, Image as ImageIcon, Description, TableChart, InsertDriveFile,
} from '@mui/icons-material';
import SavingOverlay from './SavingOverlay';
import ConfirmModal from './modals/ConfirmModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientFilesService,
  PatientFileRow,
  PatientFileTypeOption,
  MAX_FILE_SIZE_BYTES,
  ALLOWED_EXTENSIONS,
  formatFileSize,
  FileAccess,
  FileAccessOptions,
  FileViewers,
  FILE_VISIBILITIES,
  FileVisibility,
} from '../services/patientFilesService';
import PatientFileAccessFields from './PatientFileAccessFields';
import PatientFileViewersList from './PatientFileViewersList';
import { colors, typography } from '../theme/designSystem';
import { filterFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';
import ClearFiltersButton from './ClearFiltersButton';
import { getDefaultTextFieldSx } from '../theme/textFieldStyles';

/**
 * Aba Arquivos do cadastro do paciente (RN-538): anexos reais (laudos, exames, autorizações)
 * guardados no MinIO. "Quem pode ver" por arquivo (RN-538d): Somente eu, Equipe profissional do
 * paciente, Todos ou Personalizado (perfis e/ou usuários); o dono e o Administrador alteram. O servidor decide quem
 * abre, baixa e exclui; a tela só reflete (`canRead`, `canDelete`, `canManage`).
 * Substitui a maquete que existia aqui, que tinha emoji como ícone e dados fixos.
 */
interface Props {
  patientId: string;
}

const fieldSx = {
  backgroundColor: '#fff',
  flex: '1 1 170px',
  minWidth: 150,
  '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '40px' },
};

const VISIBILITY_ICONS: Record<FileVisibility, React.ReactElement> = {
  author: <Lock sx={{ fontSize: 15 }} />,
  team: <Groups sx={{ fontSize: 16 }} />,
  all: <Public sx={{ fontSize: 15 }} />,
  custom: <Tune sx={{ fontSize: 15 }} />,
};

/** Rótulo de "Quem pode ver" com o ícone da legenda — o mesmo par na legenda e na linha. */
const VisibilityTag: React.FC<{ value: FileVisibility }> = ({ value }) => {
  const option = FILE_VISIBILITIES.find(v => v.value === value);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary, fontSize: '0.8rem' }}>
      {VISIBILITY_ICONS[value]}
      {option?.short ?? value}
    </Box>
  );
};

/**
 * Legenda de "Quem pode ver": chave para ler a coluna. Cada item explica a regra num toque
 * (tooltip que abre também no celular, sem depender de passar o mouse).
 */
const VisibilityLegend: React.FC = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 2, rowGap: 0.5, mb: 1, px: 0.5 }}>
    <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
      Quem pode ver:
    </Typography>
    {FILE_VISIBILITIES.map(v => (
      <Tooltip key={v.value} title={v.help} enterTouchDelay={0} leaveTouchDelay={4000} arrow>
        <Box component="span" tabIndex={0} sx={{ cursor: 'help', outline: 'none', '&:focus-visible': { textDecoration: 'underline' } }}>
          <VisibilityTag value={v.value} />
        </Box>
      </Tooltip>
    ))}
    <Typography component="span" sx={{ fontSize: '0.75rem', color: colors.textSecondary, fontStyle: 'italic' }}>
      O Administrador abre todos os arquivos.
    </Typography>
  </Box>
);

const Dot: React.FC = () => (
  <Typography component="span" sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>•</Typography>
);

/** Ícone pela extensão: o formato é a primeira informação que se procura numa lista de anexos. */
const iconFor = (name: string): React.ReactElement => {
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase();
  if (ext === '.pdf') return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
  if (['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.heic'].includes(ext)) {
    return <ImageIcon sx={{ color: '#7b1fa2' }} />;
  }
  if (['.xls', '.xlsx', '.ods', '.csv'].includes(ext)) return <TableChart sx={{ color: '#2e7d32' }} />;
  if (['.doc', '.docx', '.odt', '.rtf', '.txt'].includes(ext)) return <Description sx={{ color: '#1565c0' }} />;
  return <InsertDriveFile sx={{ color: colors.textSecondary }} />;
};

/** O navegador exibe PDF e imagem; o resto só faz sentido baixar. */
const canPreview = (contentType: string, name: string): boolean => {
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase();
  return contentType === 'application/pdf' || ext === '.pdf'
    || contentType.startsWith('image/')
    || ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp'].includes(ext);
};

const DEFAULT_ACCESS: FileAccess = { visibility: 'author', profileIds: [], userIds: [] };

const formatDateTime = (iso: string): string => {
  if (!iso) return '—';
  const [date, time] = iso.split('T');
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y}${time ? ` ${time.slice(0, 5)}` : ''}`;
};

const PatientFilesTab: React.FC<Props> = ({ patientId }) => {
  const [items, setItems] = useState<PatientFileRow[]>([]);
  const [types, setTypes] = useState<PatientFileTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // Contador de enter/leave: os filhos da área disparam dragleave e apagariam o realce
  const dragDepth = useRef(0);

  const [uploadAccess, setUploadAccess] = useState<FileAccess>(DEFAULT_ACCESS);
  const [canManagePatientAccess, setCanManagePatientAccess] = useState(false);
  const [accessOptions, setAccessOptions] = useState<FileAccessOptions | null>(null);

  // Edição de "Quem pode ver": de um arquivo, ou de todos os arquivos do paciente ('patient')
  const [accessTarget, setAccessTarget] = useState<PatientFileRow | 'patient' | null>(null);
  const [accessValue, setAccessValue] = useState<FileAccess>(DEFAULT_ACCESS);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  // "Quem abre hoje" (nomes): segue a regra gravada ou, para quem gerencia, a que está sendo escolhida
  const [viewers, setViewers] = useState<FileViewers | null>(null);
  const [viewersLoading, setViewersLoading] = useState(false);
  const viewersSeq = useRef(0);
  const accessManaging = accessTarget === 'patient' || (accessTarget !== null && accessTarget.canManage);

  const [deleteTarget, setDeleteTarget] = useState<PatientFileRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const { toast, showToast, hideToast } = useToast();

  const hasFilters = !!(typeFilter || search.trim() || from || to);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await patientFilesService.getByPatient(patientId, {
        fileTypeId: typeFilter || undefined,
        search: search.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
      });
      setItems(data.items);
      setTypes(data.types);
      setCanManagePatientAccess(!!data.canManagePatientAccess);
    } catch {
      setError('Não foi possível carregar os arquivos deste paciente.');
    } finally {
      setLoading(false);
    }
  }, [patientId, typeFilter, search, from, to]);

  // Filtros reativos, sem botão de buscar (padrão das demais listas)
  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const clearFilters = () => {
    setTypeFilter('');
    setSearch('');
    setFrom('');
    setTo('');
  };

  // Perfis e usuários da clínica: carregados uma vez, quando alguém vai escolher quem pode ver
  const ensureAccessOptions = useCallback(async (): Promise<FileAccessOptions> => {
    if (accessOptions) return accessOptions;
    const data = await patientFilesService.getAccessOptions(patientId);
    setAccessOptions(data);
    return data;
  }, [accessOptions, patientId]);

  const openUpload = (dropped?: File) => {
    setSelectedFile(null);
    setUploadType('');
    setUploadDescription('');
    setUploadAccess(DEFAULT_ACCESS);
    setUploadError(null);
    setUploadOpen(true);
    ensureAccessOptions().catch(() => undefined);
    if (dropped) {
      pickFile(dropped);
    }
  };

  // Arrastar não substitui o modal: solta o arquivo e o modal abre com ele já escolhido,
  // porque tipo e descrição continuam sendo informados ali.
  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current += 1;
      if (e.dataTransfer.types.includes('Files')) setDragOver(true);
    },
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDragLeave: (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) setDragOver(false);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) {
        if (uploadOpen) {
          pickFile(file);
        } else {
          openUpload(file);
        }
      }
    },
  };

  // Validação espelhada do servidor: evita subir 20 MB para receber a recusa depois
  const pickFile = (file: File | null) => {
    setUploadError(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setUploadError(`Arquivos ${ext || 'sem extensão'} não são aceitos.`);
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError(`O arquivo tem ${formatFileSize(file.size)} e o limite é ${formatFileSize(MAX_FILE_SIZE_BYTES)}.`);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (uploadAccess.visibility === 'custom' && uploadAccess.profileIds.length === 0 && uploadAccess.userIds.length === 0) {
      setUploadError('Em Personalizado, escolha ao menos um perfil ou usuário.');
      return;
    }
    setUploading(true);
    try {
      await patientFilesService.upload(patientId, selectedFile, uploadType || null, uploadDescription, uploadAccess);
      setUploadOpen(false);
      showToast('Arquivo anexado.', 'success');
      await load();
    } catch {
      setUploadError('Não foi possível anexar o arquivo. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (item: PatientFileRow) => {
    setBusyId(item.id);
    try {
      await patientFilesService.download(item.id, item.name);
    } catch {
      showToast('Não foi possível baixar o arquivo.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handlePreview = async (item: PatientFileRow) => {
    setBusyId(item.id);
    try {
      await patientFilesService.openInNewTab(item.id);
    } catch {
      showToast('Não foi possível abrir o arquivo.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const loadViewers = useCallback(async (fileId: string, preview?: FileAccess) => {
    const seq = ++viewersSeq.current;
    setViewersLoading(true);
    try {
      const data = await patientFilesService.getViewers(fileId, preview);
      if (seq === viewersSeq.current) setViewers(data);
    } catch {
      if (seq === viewersSeq.current) setViewers(null);
    } finally {
      if (seq === viewersSeq.current) setViewersLoading(false);
    }
  }, []);

  const openAccess = async (target: PatientFileRow | 'patient') => {
    setAccessTarget(target);
    setAccessError(null);
    setAccessValue(DEFAULT_ACCESS);
    setViewers(null);
    const manages = target === 'patient' || target.canManage;
    if (target !== 'patient') void loadViewers(target.id);
    // Quem não gerencia só consulta a lista: a API nem expõe as concessões a ele
    if (!manages) return;
    setAccessLoading(true);
    try {
      await ensureAccessOptions();
      const current = target === 'patient'
        ? await patientFilesService.getPatientAccess(patientId)
        : await patientFilesService.getAccess(target.id);
      setAccessValue(current);
    } catch {
      setAccessError('Não foi possível carregar quem pode ver.');
    } finally {
      setAccessLoading(false);
    }
  };

  // Prévia ao vivo: a lista acompanha a regra escolhida, com uma pausa curta para não disparar a cada clique
  useEffect(() => {
    if (!accessTarget || accessTarget === 'patient' || !accessTarget.canManage || accessLoading) return undefined;
    const timer = setTimeout(() => { void loadViewers(accessTarget.id, accessValue); }, 300);
    return () => clearTimeout(timer);
  }, [accessTarget, accessValue, accessLoading, loadViewers]);

  const closeAccess = () => {
    if (!accessSaving) setAccessTarget(null);
  };

  const handleSaveAccess = async () => {
    if (!accessTarget) return;
    if (accessTarget !== 'patient' && accessValue.visibility === 'custom'
      && accessValue.profileIds.length === 0 && accessValue.userIds.length === 0) {
      setAccessError('Em Personalizado, escolha ao menos um perfil ou usuário.');
      return;
    }
    setAccessSaving(true);
    try {
      if (accessTarget === 'patient') {
        await patientFilesService.setPatientAccess(patientId, { profileIds: accessValue.profileIds, userIds: accessValue.userIds });
      } else {
        await patientFilesService.setAccess(accessTarget.id, accessValue);
      }
      setAccessTarget(null);
      showToast('Quem pode ver atualizado.', 'success');
      await load();
    } catch {
      setAccessError('Não foi possível salvar. Tente novamente.');
    } finally {
      setAccessSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await patientFilesService.remove(deleteTarget.id);
      setDeleteTarget(null);
      showToast('Arquivo excluído.', 'success');
      await load();
    } catch {
      showToast('Não foi possível excluir o arquivo.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      {...dropHandlers}
      className="tab-content-section"
      sx={{
        position: 'relative',
        outline: dragOver ? `2px dashed ${colors.primary}` : 'none',
        outlineOffset: '8px',
        borderRadius: '8px',
        transition: 'outline-color 120ms',
      }}
    >
      {dragOver && (
        <Box
          sx={{
            position: 'absolute', inset: 0, zIndex: 5, borderRadius: '8px',
            backgroundColor: 'rgba(3, 180, 198, 0.06)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CloudUpload sx={{ fontSize: 42, color: colors.primary }} />
            <Typography sx={{ fontWeight: 600, color: colors.primary }}>
              Solte para anexar
            </Typography>
          </Box>
        </Box>
      )}


      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nome ou descrição"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        />
        <TextField
          select
          size="small"
          label="Tipo"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {types.map(t => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="De"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        {canManagePatientAccess && (
          <Tooltip title="Liberar todos os arquivos deste paciente">
            <span>
              <IconButton
                onClick={() => openAccess('patient')}
                disabled={loading}
                aria-label="Liberar todos os arquivos deste paciente"
                sx={filterActionButtonSx}
              >
                <Groups />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Anexar arquivo">
          <span>
            <IconButton
              onClick={() => openUpload()}
              disabled={loading}
              sx={filterActionButtonSx}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
          {loading ? 'Carregando...' : `Encontrados: ${items.length} ${items.length === 1 ? 'item' : 'itens'}`}
          {hasFilters && !loading ? ' no filtro' : ''}
        </Typography>
      </Box>

      {/* Área de soltar VISÍVEL e sempre acima da lista: a aba inteira aceita o arquivo, mas
          sem a caixa ninguém descobre que dá para arrastar. Fica no topo porque, embaixo, se
          afastaria conforme a lista cresce. Vazia ela é maior e faz as vezes de estado vazio;
          com arquivos, encolhe para não empurrar a lista. */}
      <Box
        onClick={() => openUpload()}
        sx={{
          border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
          backgroundColor: dragOver ? 'rgba(3, 180, 198, 0.06)' : colors.background,
          borderRadius: '8px',
          padding: items.length === 0 ? '2rem 1rem' : '0.75rem 1rem',
          textAlign: 'center',
          cursor: 'pointer',
          mb: 2,
          transition: 'border-color 120ms, background-color 120ms, padding 120ms',
          '&:hover': { borderColor: colors.primary },
        }}
      >
        <CloudUpload sx={{ fontSize: items.length === 0 ? 40 : 22, color: colors.primary, mb: items.length === 0 ? 0.5 : 0, verticalAlign: 'middle', mr: items.length === 0 ? 0 : 1 }} />
        {items.length === 0 ? (
          <>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text }}>
              {hasFilters
                ? 'Nenhum arquivo encontrado com esses filtros'
                : 'Nenhum arquivo anexado a este paciente ainda'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary, mt: 0.5 }}>
              Arraste um arquivo para cá ou clique para escolher — PDF, imagem, texto ou
              planilha, até {formatFileSize(MAX_FILE_SIZE_BYTES)}
            </Typography>
          </>
        ) : (
          <Typography component="span" sx={{ fontSize: '0.85rem', color: colors.textSecondary, verticalAlign: 'middle' }}>
            Arraste um arquivo para cá ou clique para anexar
          </Typography>
        )}
      </Box>

      {error && (
        <Typography sx={{ color: colors.error ?? '#dc3545', fontSize: typography.fontSize.sm, mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: colors.primary }} />
        </Box>
      ) : items.length === 0 ? null : (
        <>
        <VisibilityLegend />
        <Box sx={{ backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          {/* Cabeçalho: toda lista tem um, e cada dado fica na sua coluna */}
          <Box className="c4u-table__header">
            <Box sx={{ flex: '1.4 1 160px', minWidth: 0 }}>Arquivo</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 130px' }}>Anexado em</Box>
            <Box className="admin-plans-header-cell c4u-col--lg" sx={{ flex: '0 0 140px' }}>Por</Box>
            <Box className="admin-plans-header-cell c4u-col--sm" sx={{ flex: '0 0 80px' }}>Tamanho</Box>
            <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '0 0 150px' }}>Quem pode ver</Box>
            <Box sx={{ flex: '0 0 140px', textAlign: 'right' }}>Ações</Box>
          </Box>

          {items.map(item => (
            <Box key={item.id} className="c4u-table__row">
              <Box sx={{ flex: '1.4 1 160px', minWidth: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ flexShrink: 0, display: 'flex' }}>{iconFor(item.name)}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.25 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: colors.text, overflowWrap: 'anywhere' }}>
                      {item.name}
                    </Typography>
                    {item.fileTypeName && (
                      <Chip label={item.fileTypeName} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                  {item.description && (
                    <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, overflowWrap: 'anywhere' }}>
                      {item.description}
                    </Typography>
                  )}
                  {/* No celular a coluna "Quem pode ver" sai; a regra continua na linha */}
                  <Box sx={{ display: 'none', '@media (max-width: 768px)': { display: 'block' } }}>
                    <VisibilityTag value={item.visibility} />
                  </Box>
                </Box>
              </Box>
              <Typography className="admin-plans-cell c4u-col--md" data-label="Anexado em" sx={{ flex: '0 0 130px', fontSize: '0.85rem', color: colors.textSecondary }}>
                {formatDateTime(item.uploadedAt)}
              </Typography>
              <Typography className="admin-plans-cell c4u-col--lg" data-label="Por" sx={{ flex: '0 0 140px', minWidth: 0, fontSize: '0.85rem', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.uploadedByName || '—'}
              </Typography>
              <Typography className="admin-plans-cell c4u-col--sm" data-label="Tamanho" sx={{ flex: '0 0 80px', fontSize: '0.85rem', color: colors.textSecondary }}>
                {formatFileSize(item.sizeBytes)}
              </Typography>
              <Box className="admin-plans-cell c4u-col--md" sx={{ flex: '0 0 150px', minWidth: 0 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => openAccess(item)}
                  title="Ver quem pode abrir este arquivo"
                  sx={{ background: 'none', border: 0, p: 0, cursor: 'pointer', textAlign: 'left', '&:hover': { textDecoration: 'underline' } }}
                >
                  <VisibilityTag value={item.visibility} />
                </Box>
              </Box>
              <Box sx={{ flex: '0 0 140px', display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {/* RN-540: sem acesso o arquivo aparece na lista — a equipe sabe que o
                    documento existe — mas não abre nem baixa. */}
                {!item.canRead ? (
                  <Tooltip title={`Conteúdo restrito por ${item.uploadedByName || 'quem anexou'}. Peça acesso a quem anexou ou ao Administrador.`}>
                    <span>
                      <IconButton size="small" disabled sx={{ color: colors.textSecondary }}>
                        <Lock fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <>
                    {canPreview(item.contentType, item.name) && (
                      <Tooltip title="Abrir em nova aba">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyId === item.id}
                            onClick={() => handlePreview(item)}
                            sx={{ color: colors.primary }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Baixar arquivo">
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyId === item.id}
                          onClick={() => handleDownload(item)}
                          sx={{ color: colors.primary }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title={item.canManage ? 'Quem pode ver' : 'Ver quem pode abrir'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openAccess(item)}
                      aria-label="Quem pode ver"
                      sx={{ color: colors.primary }}
                    >
                      <ManageAccounts fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={item.canDelete ? 'Excluir arquivo' : 'Só quem enviou o arquivo ou o Administrador pode excluir'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!item.canDelete}
                      onClick={() => setDeleteTarget(item)}
                      sx={{ color: colors.error ?? '#dc3545' }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              </Box>
            </Box>
          ))}
        </Box>
        </>
      )}

      <Dialog
        open={uploadOpen}
        onClose={() => !uploading && setUploadOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1.4rem' }}>
            Anexar arquivo
          </Typography>
          <IconButton onClick={() => setUploadOpen(false)} disabled={uploading} sx={{ color: colors.white }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={e => pickFile(e.target.files?.[0] ?? null)}
          />

          {/* Clicar e arrastar caem no mesmo lugar: a área é o alvo do drop e também o botão */}
          <Box
            {...dropHandlers}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? colors.primary : colors.border}`,
              backgroundColor: dragOver ? 'rgba(3, 180, 198, 0.06)' : colors.background,
              borderRadius: '8px',
              padding: '1.5rem',
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover': { borderColor: colors.primary },
            }}
          >
            {selectedFile ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                {iconFor(selectedFile.name)}
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text, overflowWrap: 'anywhere' }}>
                  {selectedFile.name}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary }}>
                  ({formatFileSize(selectedFile.size)})
                </Typography>
              </Box>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 40, color: colors.primary, mb: 0.5 }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: colors.text }}>
                  Arraste o arquivo aqui ou clique para escolher
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: 0.5 }}>
                  PDF, imagem, texto ou planilha, até {formatFileSize(MAX_FILE_SIZE_BYTES)}
                </Typography>
              </>
            )}
            {selectedFile && (
              <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary, mt: 1 }}>
                Clique ou arraste outro para trocar
              </Typography>
            )}
          </Box>

          {uploadError && (
            <Typography sx={{ color: colors.error ?? '#dc3545', fontSize: '0.85rem', mb: 2 }}>
              {uploadError}
            </Typography>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label="Tipo"
            value={uploadType}
            onChange={e => setUploadType(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ ...getDefaultTextFieldSx(), mb: 2 }}
          >
            <MenuItem value="">Sem classificação</MenuItem>
            {types.map(t => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            size="small"
            label="Descrição"
            placeholder="O que é este arquivo"
            value={uploadDescription}
            onChange={e => setUploadDescription(e.target.value)}
            inputProps={{ maxLength: 200 }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...getDefaultTextFieldSx(), mb: 2 }}
          />

          <PatientFileAccessFields
            value={uploadAccess}
            onChange={setUploadAccess}
            profiles={accessOptions?.profiles ?? []}
            users={accessOptions?.users ?? []}
            disabled={uploading}
          />
        </DialogContent>

        <DialogActions
          sx={{
            padding: '1.5rem 2rem',
            borderTop: `1px solid ${colors.backgroundAlt}`,
            backgroundColor: colors.background,
            gap: '1rem',
          }}
        >
          <Button
            onClick={() => setUploadOpen(false)}
            disabled={uploading}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: '#029AAB' } }}
          >
            Anexar
          </Button>
        </DialogActions>
        <SavingOverlay open={uploading} message="Enviando arquivo..." />
      </Dialog>

      <Dialog
        open={accessTarget !== null}
        onClose={closeAccess}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: colors.white,
            padding: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1.4rem' }}>
            {accessTarget === 'patient' ? 'Liberar todos os arquivos' : 'Quem pode ver'}
          </Typography>
          <IconButton onClick={closeAccess} disabled={accessSaving} sx={{ color: colors.white }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
          <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary, mb: 2, overflowWrap: 'anywhere' }}>
            {accessTarget === 'patient'
              ? 'Os perfis e usuários escolhidos abrem todos os arquivos deste paciente, de qualquer autor.'
              : accessTarget ? accessTarget.name : ''}
          </Typography>

          {accessManaging && (accessLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={26} sx={{ color: colors.primary }} />
            </Box>
          ) : (
            <PatientFileAccessFields
              value={accessValue}
              onChange={setAccessValue}
              profiles={accessOptions?.profiles ?? []}
              users={accessOptions?.users ?? []}
              profilesAndUsersOnly={accessTarget === 'patient'}
              disabled={accessSaving}
            />
          ))}

          {accessTarget !== 'patient' && (
            <Box sx={{ mt: accessManaging ? 2.5 : 0 }}>
              <PatientFileViewersList viewers={viewers} loading={viewersLoading} />
            </Box>
          )}

          {accessError && (
            <Typography sx={{ color: colors.error ?? '#dc3545', fontSize: '0.85rem', mt: 2 }}>
              {accessError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: '1.5rem 2rem',
            borderTop: `1px solid ${colors.backgroundAlt}`,
            backgroundColor: colors.background,
            gap: '1rem',
          }}
        >
          <Button onClick={closeAccess} disabled={accessSaving} variant="outlined" sx={{ textTransform: 'none' }}>
            {accessManaging ? 'Cancelar' : 'Fechar'}
          </Button>
          {accessManaging && (
            <Button
              variant="contained"
              onClick={handleSaveAccess}
              disabled={accessSaving || accessLoading}
              sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: '#029AAB' } }}
            >
              Salvar
            </Button>
          )}
        </DialogActions>
        <SavingOverlay open={accessSaving} message="Salvando..." />
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir arquivo"
        message={`Excluir "${deleteTarget?.name}"? O arquivo sai da lista e é removido do armazenamento.`}
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        type="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        isProcessing={deleting}
      />

      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </Box>
  );
};

export default PatientFilesTab;
